import React, { useEffect, useState, useMemo } from "react";
import { 
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, 
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend 
} from "recharts";
import axios from "axios";
import Sidebar from "../Sidebar/Sidebar";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartBar, faFilter, faCalendarAlt, faIndianRupeeSign, 
  faReceipt, faExchangeAlt, faClock, faWallet, faUsers, faChartPie 
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from "../../config/api.config";

const COLORS = ['#FF4135', '#35A2FF', '#FF9800', '#00D1B2', '#9C27B0'];

const Graph = () => {
  const [rawData, setRawData] = useState([]);
  const [selectedYear, setSelectedYear] = useState('All');
  const [availableYears, setAvailableYears] = useState([]);
  const [viewMode, setViewMode] = useState('revenue');

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const token = localStorage.getItem('token');
        const userDetails = JSON.parse(localStorage.getItem("userDetails"));
        const selectedRestId = localStorage.getItem("currentRestaurantId");
        let activeRestaurantId = userDetails?.role === 'owner' ? selectedRestId : userDetails?.restaurantId;

        // Don't make API call if no token
        if (!token) {
          setRawData([]);
          return;
        }

        // Handle populated restaurantId object
        if (activeRestaurantId && typeof activeRestaurantId === 'object') {
            activeRestaurantId = activeRestaurantId._id;
        }

        if (!activeRestaurantId) {
          setRawData([]);
          return;
        }

        const response = await axios.get(buildApiUrl(`/invoice/admin/all-invoice?restaurantId=${activeRestaurantId}`), {
          headers: { 'auth-token': token }
        });
        const years = new Set(response.data.map(inv => new Date(inv.invoiceDate).getFullYear()));
        setAvailableYears([...years].sort((a, b) => b - a));
        setRawData(response.data);
      } catch (error) {
        if (error.response?.status === 401) {
          console.log('Token expired or invalid. Please login again.');
        } else {
          console.error("Error fetching invoice data:", error);
        }
      }
    };
    fetchInvoices();
  }, []);

  const { 
    chartData, 
    totalSales, 
    totalOrders, 
    averageOrderValue,
    peakHoursData,
    paymentMethodsData,
    loyaltyRate
  } = useMemo(() => {
    const filteredInvoices = selectedYear === 'All' 
      ? rawData 
      : rawData.filter(inv => new Date(inv.invoiceDate).getFullYear() === Number(selectedYear));

    const monthlyData = {};
    const hourMap = new Array(24).fill(0);
    const methodMap = {};
    const customerVisits = {};
    let totalRevenue = 0;
    let totalCount = 0;

    filteredInvoices.forEach((invoice) => {
      const date = new Date(invoice.invoiceDate);
      const amount = Number(invoice.finalAmount || invoice.totalAmount || 0);
      
      // Main Trend Data
      const year = date.getFullYear();
      const monthIndex = date.getMonth();
      const key = `${year}-${monthIndex}`;
      if (!monthlyData[key]) monthlyData[key] = { revenue: 0, count: 0 };
      monthlyData[key].revenue += amount;
      monthlyData[key].count += 1;
      
      // Peak Hours (0-23)
      const hour = date.getHours();
      hourMap[hour] += 1; // Count orders per hour

      // Payment Methods - Check invoice structure for paymentMethod or iterate payments array
      // Assuming invoice has paymentMethod or mapping from payments array
      let method = 'Unknown';
      if (invoice.payments && invoice.payments.length > 0) {
         method = invoice.payments[0].paymentMethod || 'Cash';
      } else if (invoice.paymentMethod) {
         method = invoice.paymentMethod;
      }
      // Normalize method names
      method = method.toLowerCase();
      if (method.includes('card')) method = 'Card';
      else if (method.includes('upi') || method.includes('qr')) method = 'UPI';
      else if (method.includes('bank')) method = 'Transfer';
      else method = 'Cash';
      
      if (!methodMap[method]) methodMap[method] = 0;
      methodMap[method] += amount;

      // Loyalty (Customer Name)
      const custName = invoice.customerName || invoice.userId?.name || 'Walk-in';
      if (custName !== 'Walk-in') {
        customerVisits[custName] = (customerVisits[custName] || 0) + 1;
      }

      totalRevenue += amount;
      totalCount += 1;
    });

    // Process Main Chart
    const processedChart = Object.keys(monthlyData).map((key) => {
      const [year, monthIndex] = key.split("-").map(Number);
      const month = new Date(year, monthIndex, 1).toLocaleString("default", { month: "short" });
      return {
        label: selectedYear === 'All' ? `${month} '${year.toString().slice(-2)}` : month,
        fullLabel: `${month} ${year}`,
        revenue: monthlyData[key].revenue,
        count: monthlyData[key].count,
        year, monthIndex,
      };
    }).sort((a, b) => a.year - b.year || a.monthIndex - b.monthIndex);

    // Process Peak Hours
    const processedPeak = hourMap.map((count, hour) => ({
      hour: `${hour}:00`,
      orders: count
    }));

    // Process Payment Methods
    const processedPayment = Object.keys(methodMap).map(method => ({
      name: method,
      value: methodMap[method]
    })).sort((a, b) => b.value - a.value);

    // Process Loyalty
    const namedCustomers = Object.keys(customerVisits);
    const returningCustomers = namedCustomers.filter(name => customerVisits[name] > 1).length;
    const loyalty = namedCustomers.length > 0 ? (returningCustomers / namedCustomers.length) * 100 : 0;
    const aov = totalCount > 0 ? totalRevenue / totalCount : 0;

    return { 
      chartData: processedChart, 
      totalSales: totalRevenue, 
      totalOrders: totalCount, 
      averageOrderValue: aov,
      peakHoursData: processedPeak,
      paymentMethodsData: processedPayment,
      loyaltyRate: loyalty
    };
  }, [rawData, selectedYear]);

  const CustomTooltip = ({ active, payload, label, prefix = '', suffix = '' }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ backgroundColor: 'var(--tf-bg-card)', padding: '10px', border: '1px solid var(--tf-border)', borderRadius: '8px', color: 'white' }}>
          <p style={{ margin: 0, fontWeight: 'bold', color: 'var(--tf-text-muted)', fontSize: '0.8rem' }}>{label}</p>
          <p style={{ margin: '5px 0 0', color: payload[0].fill || 'white', fontSize: '0.9rem' }}>
            {prefix}{payload[0].value.toLocaleString()}{suffix}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="admin-layout">
      <Sidebar />
      <main style={{ overflowY: 'auto', height: '100vh', paddingBottom: '2rem' }}>
        <div className="tf-container">
          {/* Header */}
          <header className="tf-page-header" style={{ marginBottom: '2rem' }}>
            <div className="tf-header-content">
              <div className="tf-header-title-group">
                <div className="tf-header-icon" style={{ background: 'rgba(53, 162, 255, 0.1)', color: 'var(--tf-secondary)' }}>
                  <FontAwesomeIcon icon={faChartBar} />
                </div>
                <div>
                  <h1 className="tf-page-title">Analytics Center</h1>
                  <p className="tf-page-subtitle">Commercial performance & actionable insights</p>
                </div>
              </div>
              <div className="tf-header-actions" style={{ gap: '1rem' }}>
                 <div className="tf-search-container" style={{ width: 'auto', minWidth: '180px' }}>
                    <FontAwesomeIcon icon={faFilter} className="tf-search-icon" style={{ left: '1rem', fontSize: '0.8rem' }} />
                    <select 
                      className="tf-search-input" 
                      style={{ paddingLeft: '2.5rem', cursor: 'pointer', appearance: 'auto' }}
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                    >
                       <option value="All">All Time History</option>
                       {availableYears.map(year => (
                         <option key={year} value={year}>Fiscal Year {year}</option>
                       ))}
                    </select>
                 </div>
              </div>
            </div>
          </header>

          {/* KPI Cards Row - Compact & High-Impact */}
          <div className="tf-status-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', marginBottom: '2rem', gap: '1.5rem' }}>
             <div className="tf-card" style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1.5rem', borderLeft: '4px solid var(--tf-success)' }}>
                <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: 'rgba(0, 209, 178, 0.1)', color: 'var(--tf-success)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                   <FontAwesomeIcon icon={faIndianRupeeSign} />
                </div>
                <div>
                   <h4 style={{ fontSize: '1.8rem', fontWeight: '800', lineHeight: '1', marginBottom: '0.3rem' }}>₹{totalSales.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</h4>
                   <p style={{ margin: 0, color: 'var(--tf-text-muted)', fontSize: '0.85rem', fontWeight: '600' }}>Total Revenue</p>
                </div>
             </div>
             <div className="tf-card" style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1.5rem', borderLeft: '4px solid var(--tf-secondary)' }}>
                <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: 'rgba(53, 162, 255, 0.1)', color: 'var(--tf-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                   <FontAwesomeIcon icon={faReceipt} />
                </div>
                <div>
                   <h4 style={{ fontSize: '1.8rem', fontWeight: '800', lineHeight: '1', marginBottom: '0.3rem' }}>{totalOrders.toLocaleString()}</h4>
                   <p style={{ margin: 0, color: 'var(--tf-text-muted)', fontSize: '0.85rem', fontWeight: '600' }}>Total Orders</p>
                </div>
             </div>
             <div className="tf-card" style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1.5rem', borderLeft: '4px solid #FF9800' }}>
                <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: 'rgba(255, 152, 0, 0.1)', color: '#FF9800', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                   <FontAwesomeIcon icon={faExchangeAlt} />
                </div>
                <div>
                   <h4 style={{ fontSize: '1.8rem', fontWeight: '800', lineHeight: '1', marginBottom: '0.3rem' }}>₹{averageOrderValue.toFixed(0)}</h4>
                   <p style={{ margin: 0, color: 'var(--tf-text-muted)', fontSize: '0.85rem', fontWeight: '600' }}>Avg. Order Value</p>
                </div>
             </div>
          </div>

          {/* MAIN DASHBOARD GRID */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2.5fr) minmax(0, 1fr)', gap: '1.5rem' }}>
             
             {/* LEFT COLUMN: Main Visuals */}
             <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                
                {/* 1. Main Trend Chart */}
                <div className="tf-card" style={{ padding: '0', overflow: 'hidden' }}>
                    <div className="tf-flex" style={{ justifyContent: 'space-between', padding: '1.5rem 1.5rem 0', marginBottom: '1rem' }}>
                      <div className="tf-flex" style={{ gap: '0.8rem' }}>
                         <FontAwesomeIcon icon={faChartBar} style={{ color: 'var(--tf-primary)' }} />
                         <h3 className="tf-heading-sm" style={{ margin: 0 }}>Business Growth</h3>
                      </div>
                      <div className="tf-flex" style={{ background: 'var(--tf-bg-input)', padding: '0.25rem', borderRadius: '8px', border: '1px solid var(--tf-border)' }}>
                        <button onClick={() => setViewMode('revenue')} style={{ padding: '0.3rem 0.8rem', borderRadius: '6px', background: viewMode === 'revenue' ? 'var(--tf-primary)' : 'transparent', color: viewMode === 'revenue' ? 'white' : 'var(--tf-text-muted)', border: 'none', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', transition: 'var(--tf-transition)' }}>Revenue</button>
                        <button onClick={() => setViewMode('volume')} style={{ padding: '0.3rem 0.8rem', borderRadius: '6px', background: viewMode === 'volume' ? 'var(--tf-secondary)' : 'transparent', color: viewMode === 'volume' ? 'white' : 'var(--tf-text-muted)', border: 'none', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', transition: 'var(--tf-transition)' }}>Volume</button>
                      </div>
                    </div>
                    <div style={{ height: '350px', padding: '0 1.5rem 1.5rem' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 10, right: 0, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                          <XAxis dataKey="label" tick={{ fill: 'var(--tf-text-muted)', fontSize: 11 }} axisLine={{ stroke: 'var(--tf-border)' }} tickLine={false} dy={10} interval={0} />
                          <YAxis tick={{ fill: 'var(--tf-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(val) => viewMode === 'revenue' ? `₹${val/1000}k` : val} />
                          <Tooltip content={<CustomTooltip prefix={viewMode === 'revenue' ? '₹' : ''} />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                          <Bar dataKey={viewMode === 'revenue' ? "revenue" : "count"} fill={viewMode === 'revenue' ? "#FF4135" : "#35A2FF"} radius={[4, 4, 0, 0]} barSize={40} activeBar={{ fill: viewMode === 'revenue' ? '#D8352B' : '#2D89EF' }} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                </div>

                {/* 2. Peak Hours Chart */}
                <div className="tf-card" style={{ padding: '0', overflow: 'hidden' }}>
                    <div className="tf-flex" style={{ gap: '0.8rem', padding: '1.5rem 1.5rem 0', marginBottom: '1rem' }}>
                       <FontAwesomeIcon icon={faClock} style={{ color: '#FF9800' }} />
                       <h3 className="tf-heading-sm" style={{ margin: 0 }}>Peak Traffic Hours</h3>
                    </div>
                    <div style={{ height: '280px', padding: '0 1.5rem 1.5rem' }}>
                       <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={peakHoursData} margin={{ top: 10, right: 0, left: -10, bottom: 0 }}>
                             <defs>
                                <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                                   <stop offset="5%" stopColor="#FF9800" stopOpacity={0.3}/>
                                   <stop offset="95%" stopColor="#FF9800" stopOpacity={0}/>
                                </linearGradient>
                             </defs>
                             <XAxis dataKey="hour" tick={{ fill: 'var(--tf-text-muted)', fontSize: 10 }} interval={3} axisLine={false} tickLine={false} dy={5} />
                             <YAxis tick={{ fill: 'var(--tf-text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} />
                             <Tooltip content={<CustomTooltip suffix=" Orders" />} />
                             <Area type="monotone" dataKey="orders" stroke="#FF9800" fillOpacity={1} fill="url(#colorOrders)" strokeWidth={2} />
                          </AreaChart>
                       </ResponsiveContainer>
                    </div>
                </div>

             </div>

             {/* RIGHT COLUMN: Widgets */}
             <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                
                {/* 1. Retention Widget */}
                <div className="tf-card" style={{ padding: '2rem', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '4px', background: 'linear-gradient(90deg, #9C27B0, #E040FB)' }}></div>
                    <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(156, 39, 176, 0.1)', color: '#9C27B0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', margin: '0 auto 1.5rem' }}>
                       <FontAwesomeIcon icon={faUsers} />
                    </div>
                    <h3 style={{ fontSize: '3rem', fontWeight: '800', margin: '0 0 0.5rem', color: '#E040FB', lineHeight: 1 }}>{loyaltyRate.toFixed(0)}%</h3>
                    <h4 style={{ margin: 0, color: 'var(--tf-text-white)', fontSize: '1.1rem' }}>Retention Rate</h4>
                    <p style={{ margin: '0.5rem 0 0', color: 'var(--tf-text-muted)', fontSize: '0.85rem' }}>of your customers return for more</p>
                </div>

                {/* 2. Payment Distribution Widget */}
                <div className="tf-card" style={{ padding: '0 1.5rem 1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div className="tf-flex" style={{ gap: '0.8rem', padding: '1.5rem 0 0', marginBottom: '1rem' }}>
                       <FontAwesomeIcon icon={faWallet} style={{ color: '#00D1B2' }} />
                       <h3 className="tf-heading-sm" style={{ margin: 0 }}>Payment Mix</h3>
                    </div>
                    <div style={{ flex: 1, minHeight: '300px', position: 'relative' }}>
                       <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                             <Pie
                                data={paymentMethodsData}
                                innerRadius={60}
                                outerRadius={85}
                                paddingAngle={5}
                                dataKey="value"
                                stroke="none"
                             >
                                {paymentMethodsData.map((entry, index) => (
                                   <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                             </Pie>
                             <Tooltip content={<CustomTooltip prefix="₹" />} />
                             <Legend 
                                verticalAlign="bottom" 
                                align="center" 
                                layout="horizontal" 
                                iconType="circle"
                                wrapperStyle={{ fontSize: '11px', color: 'var(--tf-text-muted)' }} 
                             />
                          </PieChart>
                       </ResponsiveContainer>
                       {/* Center Text for Doughnut */}
                       <div style={{ position: 'absolute', top: '42%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                          <span style={{ fontSize: '0.7rem', color: 'var(--tf-text-muted)', textTransform: 'uppercase' }}>Volume</span>
                          <div style={{ fontSize: '1.2rem', fontWeight: '800', color: 'var(--tf-text-white)' }}>₹{(totalSales / 1000).toFixed(1)}k</div>
                       </div>
                    </div>
                </div>

             </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default Graph;
